import React, { useCallback, useState } from "react";
import CommandePage from "../../core/commande/CommandePage";
import PatientForm from "../../components/PatientForm";
import { dashboardContext } from "./dashboard.context";
import { API } from "../../services/api";
import { fromBackendToDraft } from "../../core/commande/model/commandeDraft";
import useMedicationScanner from "./useMedicationScanner";

function mergeValidationState(previous, response) {
    if (!previous || !response) return previous;
    const patients = new Map((response.patients || []).map(patient => [patient.commandePatientId, patient]));
    return {
        ...previous,
        managementStatut: response.managementStatut ?? previous.managementStatut,
        statut: response.statut ?? previous.statut,
        patients: (previous.patients || []).map(patient => {
            const fresh = patients.get(patient.commandePatientId);
            if (!fresh) return patient;
            const products = new Map((fresh.produits || []).map(product => [product.id, product]));
            return {
                ...patient,
                managementValidated: fresh.managementValidated,
                managementValidatedAt: fresh.managementValidatedAt,
                managementValidatedBy: fresh.managementValidatedBy,
                pharmaceuticalValidated: fresh.pharmaceuticalValidated,
                produits: (patient.produits || []).map(product => ({ ...product, ...(products.get(product.id) || {}) })),
            };
        }),
    };
}

function samePatientIdentity(left, right) {
    const normalize = value => String(value || "").trim().toLowerCase();
    return normalize(left?.nom) === normalize(right?.nom)
        && normalize(left?.prenom) === normalize(right?.prenom)
        && (left?.dateNaissance ?? null) === (right?.dateNaissance ?? null);
}

function findSavedPatient(patients, original) {
    if (original?.commandePatientId != null) {
        const byLink = (patients || []).find(patient => patient.commandePatientId === original.commandePatientId);
        if (byLink) return byLink;
    }
    return (patients || []).find(patient => samePatientIdentity(patient, original)) || null;
}

function scanErrorMessage(error) {
    return error?.response?.data?.message || "Le scan n'a pas pu être enregistré.";
}

function scanSuccessMessage(scan) {
    const expiry = scan?.expiryDate
        ? new Intl.DateTimeFormat("fr-FR").format(new Date(`${scan.expiryDate}T00:00:00`))
        : "inconnue";
    return `${scan?.medicamentName || "Médicament"} ajouté — quantité ${scan?.quantity ?? "?"}, lot ${scan?.lotNumber || "?"}, DLU ${expiry}`;
}

function ScannerStatus({ feedback, scanning }) {
    const isError = feedback?.type === "error";
    return (
        <div
            role={isError ? "alert" : "status"}
            aria-live="polite"
            style={{
                margin: "8px 12px 0",
                padding: "9px 12px",
                borderRadius: "8px",
                border: `1px solid ${isError ? "#fecaca" : "#a7f3d0"}`,
                background: isError ? "#fef2f2" : "#ecfdf5",
                color: isError ? "#991b1b" : "#0C3B3C",
                fontSize: "13px",
                fontWeight: 650,
            }}
        >
            {scanning ? "Lecture de la boîte en cours…" : feedback?.message || "Scanner prêt — scannez la boîte hors d’un champ de saisie"}
        </div>
    );
}

function ManagementEditor({ draft, setDraft, setWorkflowDraft, save, isDirty }) {
    const [selection, setSelection] = useState({ active: false, ids: new Set(), loading: false });
    const [selectedPatient, setSelectedPatient] = useState(() => draft?.patients?.[0] || null);
    const [scanState, setScanState] = useState({ scanning: false, feedback: null });

    const applyResponse = response => setDraft(previous => mergeValidationState(previous, response.data));
    const ensureSaved = async () => !isDirty || await save();

    const scanMedication = useCallback(async rawCode => {
        if (!selectedPatient || (draft?.patients || []).length === 0) {
            setScanState({ scanning: false, feedback: { type: "error", message: "Veuillez d'abord ajouter un patient" } });
            return;
        }
        setScanState(previous => ({ ...previous, scanning: true }));
        try {
            let savedPatient = selectedPatient;
            if (isDirty || !savedPatient.commandePatientId) {
                if (!await save()) {
                    setScanState({ scanning: false, feedback: { type: "error", message: "La commande doit être sauvegardée avant le scan." } });
                    return;
                }
                const fresh = await API.get(`/commandes/${draft.id}`, {
                    params: { _ts: Date.now() },
                    headers: { "Cache-Control": "no-cache" },
                });
                savedPatient = findSavedPatient(fresh.data?.patients, selectedPatient);
            }
            if (!savedPatient?.commandePatientId) {
                setScanState({ scanning: false, feedback: { type: "error", message: "Le patient courant n'a pas pu être enregistré." } });
                return;
            }
            const response = await API.post(
                `/commandes/${draft.id}/patients/${savedPatient.commandePatientId}/medicament-scans`,
                { rawCode },
                { noRedirectOn401: true }
            );
            if (response.data?.commande) {
                setWorkflowDraft(fromBackendToDraft(response.data.commande));
            }
            setScanState({ scanning: false, feedback: { type: "success", message: scanSuccessMessage(response.data?.scan) } });
        } catch (error) {
            setScanState({ scanning: false, feedback: { type: "error", message: scanErrorMessage(error) } });
        }
    }, [draft, isDirty, save, selectedPatient, setWorkflowDraft]);

    useMedicationScanner({ enabled: !scanState.scanning, onScan: scanMedication });

    const validatePatient = async patient => {
        let savedPatient = patient;
        if (isDirty) {
            if (!await save()) return false;
            const fresh = await API.get(`/commandes/${draft.id}`, {
                params: { _ts: Date.now() },
                headers: { "Cache-Control": "no-cache" },
            });
            savedPatient = findSavedPatient(fresh.data?.patients, patient);
            if (!savedPatient?.commandePatientId) return false;
        }
        const response = await API.post(
            `/commandes/${draft.id}/patients/${savedPatient.commandePatientId}/validation-gestion`,
            null,
            { noRedirectOn401: true }
        );
        applyResponse(response);
        return true;
    };

    const toggleProduct = id => {
        if (id == null) return;
        setSelection(previous => {
            const ids = new Set(previous.ids);
            if (ids.has(id)) ids.delete(id); else ids.add(id);
            return { ...previous, ids };
        });
    };

    const validateProducts = async () => {
        if (!selection.active) {
            if (!await ensureSaved()) return;
            setSelection({ active: true, ids: new Set(), loading: false });
            return;
        }
        if (selection.ids.size === 0) return;
        setSelection(previous => ({ ...previous, loading: true }));
        try {
            const response = await API.post(
                `/commandes/${draft.id}/produits/validation-gestion`,
                { medicamentIds: [...selection.ids] },
                { noRedirectOn401: true }
            );
            applyResponse(response);
            setSelection({ active: false, ids: new Set(), loading: false });
        } finally {
            setSelection(previous => ({ ...previous, loading: false }));
        }
    };

    const productAction = (
        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
            {selection.active && (
                <button type="button" onClick={() => setSelection({ active: false, ids: new Set(), loading: false })}>
                    Annuler
                </button>
            )}
            <button
                type="button"
                onClick={validateProducts}
                disabled={selection.loading || (selection.active && selection.ids.size === 0)}
                style={{ padding: "10px 16px", border: 0, borderRadius: "8px", background: "#0C3B3C", color: "white", fontWeight: 700 }}
            >
                {selection.loading ? "Validation..." : selection.active ? `Valider les ${selection.ids.size} produits` : "Valider des produits"}
            </button>
        </div>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <ScannerStatus feedback={scanState.feedback} scanning={scanState.scanning} />
            <div style={{ flex: 1, minHeight: 0 }}>
            <PatientForm
            patients={draft?.patients || []}
            ehpadId={draft?.ehpadId}
            medicamentStrategy={dashboardContext.medicamentStrategy}
            onChange={patients => setDraft(previous => ({ ...previous, patients }))}
            onSave={save}
            isDirty={isDirty}
            onValidatePatient={validatePatient}
            patientValidationField="managementValidated"
            patientValidationLabel="Valider la saisie"
            productSelectionMode={selection.active}
            selectedProductIds={selection.ids}
            onToggleProductSelection={toggleProduct}
            productValidationAction={productAction}
            onSelectedPatientChange={setSelectedPatient}
        />
            </div>
        </div>
    );
}

export default function Dashboard() {
    return (
        <CommandePage
            context={dashboardContext}
            renderDetailActions={({ commande, saveDraft, isDirty, goToList, refresh }) => (
                commande.managementStatut !== "VALIDE" ? (
                    <button
                        type="button"
                        onClick={async () => {
                            if (isDirty && !await saveDraft()) return;
                            await API.post(`/commandes/${commande.id}/validation-gestion`, null, { noRedirectOn401: true });
                            goToList();
                            refresh();
                        }}
                        style={{ padding: "7px 14px", border: 0, borderRadius: "7px", background: "#17B978", color: "#122A26", fontWeight: 800 }}
                    >
                        Valider toute la saisie
                    </button>
                ) : null
            )}
        >
            {({ draft, setDraft, setWorkflowDraft, save, isDirty }) => (
                <ManagementEditor draft={draft} setDraft={setDraft} setWorkflowDraft={setWorkflowDraft} save={save} isDirty={isDirty} />
            )}
        </CommandePage>
    );
}
